import {
  buildMotorHandbookFlatIndex,
  buildMotorNameSlugIndex,
  motorHandbookModelKey,
  normalizeMotorLookupToken,
} from '@/lib/motorTechReference';

export type MotorHandbookBulkParseReport = {
  applied: { line: number; key: string; url: string; via: 'key' | 'name' | 'section_name' }[];
  ambiguous: { line: number; raw: string; keys: string[] }[];
  unknown: { line: number; raw: string }[];
  invalid: { line: number; raw: string; reason: string }[];
};

const URL_RE = /^https?:\/\/.+/i;

function splitLine(raw: string): { left: string; url: string } | null {
  const line = raw.trim();
  if (!line || line.startsWith('#')) return null;

  const eq = line.match(/^(.+?)\s*=\s*(https?:\/\/.+)$/i);
  if (eq) return { left: eq[1].trim(), url: eq[2].trim() };

  const tab = line.split('\t');
  if (tab.length >= 2) {
    const url = tab[tab.length - 1].trim();
    const left = tab.slice(0, -1).join('\t').trim();
    if (URL_RE.test(url)) return { left, url };
  }

  const comma = line.lastIndexOf(',');
  if (comma > 0) {
    const left = line.slice(0, comma).trim();
    const url = line.slice(comma + 1).trim();
    if (URL_RE.test(url)) return { left, url };
  }

  const space = line.match(/^(.+?)\s+(https?:\/\/.+)$/i);
  if (space) return { left: space[1].trim(), url: space[2].trim() };

  return null;
}

function resolveKey(
  left: string,
  keySet: Set<string>,
  nameIndex: Map<string, string[]>,
  bySectionName: Map<string, string>,
): string | 'ambiguous' | null {
  const trimmed = left.trim();
  if (!trimmed) return null;

  if (keySet.has(trimmed)) return trimmed;

  const pipe = trimmed.match(/^([^|/]+)[|/](.+)$/);
  if (pipe) {
    const sectionPart = pipe[1].trim();
    const namePart = pipe[2].trim();
    const sectionSlug = normalizeMotorLookupToken(sectionPart);
    const key = motorHandbookModelKey(sectionSlug, namePart);
    if (keySet.has(key)) return key;
    const composite = `${sectionSlug}::${normalizeMotorLookupToken(namePart)}`;
    const hit = bySectionName.get(composite);
    if (hit) return hit;
  }

  const dot = trimmed.includes('.');
  if (dot && keySet.has(trimmed.toLowerCase())) return trimmed.toLowerCase();

  const slug = normalizeMotorLookupToken(trimmed);
  const hits = nameIndex.get(slug);
  if (!hits?.length) return null;
  if (hits.length === 1) return hits[0];
  return 'ambiguous';
}

export function parseMotorHandbookBulkLines(text: string): {
  updates: Record<string, string>;
  report: MotorHandbookBulkParseReport;
} {
  const flat = buildMotorHandbookFlatIndex();
  const keySet = new Set(flat.map((e) => e.key));
  const nameIndex = buildMotorNameSlugIndex();
  const bySectionName = new Map<string, string>();
  for (const e of flat) {
    const nameSlug = normalizeMotorLookupToken(e.modelName);
    bySectionName.set(`${e.sectionId}::${nameSlug}`, e.key);
    bySectionName.set(`${normalizeMotorLookupToken(e.sectionTitle)}::${nameSlug}`, e.key);
  }

  const report: MotorHandbookBulkParseReport = {
    applied: [],
    ambiguous: [],
    unknown: [],
    invalid: [],
  };
  const updates: Record<string, string> = {};

  const lines = text.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const parsed = splitLine(raw);
    if (!parsed) continue;

    const { left, url } = parsed;
    if (!URL_RE.test(url)) {
      report.invalid.push({ line: i + 1, raw, reason: 'Некорректный URL' });
      continue;
    }

    const resolved = resolveKey(left, keySet, nameIndex, bySectionName);
    if (resolved === 'ambiguous') {
      const slug = normalizeMotorLookupToken(left);
      report.ambiguous.push({ line: i + 1, raw, keys: nameIndex.get(slug) || [] });
      continue;
    }
    if (!resolved) {
      report.unknown.push({ line: i + 1, raw });
      continue;
    }

    const via: 'key' | 'name' | 'section_name' =
      left === resolved ? 'key' : left.includes('|') || left.includes('/') ? 'section_name' : 'name';

    updates[resolved] = url;
    report.applied.push({ line: i + 1, key: resolved, url, via });
  }

  return { updates, report };
}
